import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { closestWithClass } from '../../utils';
import s from '../../styles/staff.module.css';

// A single-choice menu that looks like the locations picker, rather than the
// browser's own select.
//
// A native select renders its list in the operating system's chrome — grey on
// macOS, square-cornered, ignoring the page's theme — so next to a styled
// multi-select showing the same kind of data the two read as different kinds
// of control when they are the same kind of choice.
//
// The list is portalled out and positioned from the button's rect, because
// inside a table it would be clipped by `.tableWrap`'s overflow — a menu
// showing only its first few rows is worse than a native one.
//
// It is portalled into the page wrapper rather than the body: the wrapper is
// what carries `.lightPage`, so the menu follows the theme, including when it
// is toggled. The wrapper sets no overflow or transform, so a fixed position
// inside it still measures against the viewport.
//
// `options` are `{ value, label, note, disabled }`. A `null`/'' value means
// nothing chosen, which `placeholder` names.
const MENU_MIN_WIDTH = 210;
const VIEWPORT_MARGIN = 8;

export function SelectMenu({
  value, options = [], onChange, placeholder = 'Choose…', disabled, menuAlign = 'left',
}) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState(null);
  const btn = useRef(null);
  const menu = useRef(null);
  const [host, setHost] = useState(null);

  // Measure against the viewport, then keep it on screen: clamp horizontally,
  // and flip above the button when there is more room up than down.
  const place = useCallback(() => {
    if (!btn.current) return;
    const r = btn.current.getBoundingClientRect();
    const width = Math.max(r.width, MENU_MIN_WIDTH);
    const below = window.innerHeight - r.bottom - VIEWPORT_MARGIN;
    const above = r.top - VIEWPORT_MARGIN;
    const flip = below < 220 && above > below;
    const left = menuAlign === 'right' ? r.right - width : r.left;

    setBox({
      left: Math.min(Math.max(VIEWPORT_MARGIN, left), window.innerWidth - width - VIEWPORT_MARGIN),
      top: flip ? undefined : r.bottom + 6,
      bottom: flip ? window.innerHeight - r.top + 6 : undefined,
      width,
      maxHeight: Math.max(160, (flip ? above : below) - 6),
    });
  }, [menuAlign]);

  useLayoutEffect(() => {
    if (!open) return;
    setHost(closestWithClass(btn.current, s.page) || document.body);
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (btn.current?.contains(e.target) || menu.current?.contains(e.target)) return;
      setOpen(false);
    };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    // Any scroll moves the button, so the menu has to follow it. `true` catches
    // scrolling inside the table as well as the page.
    const follow = () => place();
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open, place]);

  const chosen = options.find(o => String(o.value) === String(value));

  if (disabled) {
    return <span className={s.selectMenuFlat}>{chosen ? chosen.label : placeholder}</span>;
  }

  return (
    <span className={s.locPick}>
      <button type="button" ref={btn} className={s.locPickBtn}
              aria-haspopup="listbox" aria-expanded={open}
              onClick={() => setOpen(o => !o)}>
        <span>{chosen ? chosen.label : placeholder}</span>
        <span className={s.locPickCaret} aria-hidden="true">▾</span>
      </button>

      {open && box && host && createPortal(
        <div ref={menu} role="listbox" className={s.selectMenuPop}
             style={{ left: box.left, top: box.top, bottom: box.bottom,
                      minWidth: box.width, maxHeight: box.maxHeight }}>
          {options.map(o => {
            const on = String(o.value) === String(value);
            return (
              <button key={String(o.value)} type="button" role="option" aria-selected={on}
                      className={`${s.locPickItem} ${on ? s.selectMenuItemOn : ''}`}
                      disabled={o.disabled}
                      onClick={() => { if (o.disabled) return; onChange(o.value); setOpen(false); }}>
                {/* The tick keeps its column whether shown or not, so labels do
                    not shuffle as the choice moves. */}
                <span className={s.selectMenuTick}>{on ? '✓' : ''}</span>
                <span className={s.selectMenuBody}>
                  <span>{o.label}</span>
                  {o.note && <span className={s.selectMenuNote}>{o.note}</span>}
                </span>
              </button>
            );
          })}
          {!options.length && (
            <span className={s.locPickNote}>Nothing to choose from.</span>
          )}
        </div>,
        host,
      )}
    </span>
  );
}
